import React, { Component } from 'react';

import PlusIcon from './PlusIcon';

import { Wrapper, TopicWrapper, Topic, ListWrapper, List, Keyword, TopicIcon } from './styled/KeyphraseStackStyled';

import StarIcon from './StarIcon';

import { words as wordsUtil } from '../../../../utils/utils';

const { getSortedDetection } = wordsUtil;

interface KeywordItem {
    text: string;
    count: number;
    points?: number;
}

interface KeyphraseStackProps {
    topic: string;
    alternatives: KeywordItem[];
    complements: KeywordItem[];
    displayDoSayAlternatives: boolean;
}

interface KeyphraseStackState {
    scrollTop: number;
    availableSpace: number;
}

class KeyphraseStack extends Component<KeyphraseStackProps, KeyphraseStackState> {
    wrapper: HTMLElement | null = null;
    scrollableContainerRef: HTMLElement[] = [];
    alternativesRef: HTMLElement | null = null;

    state = {
        scrollTop: 0,
        availableSpace: 240
    };

    _handleScroll = () => {
        let scrollTop = 0;
        if (this.scrollableContainerRef && Array.isArray(this.scrollableContainerRef)) {
            this.scrollableContainerRef.forEach((ref: HTMLElement) => {
                scrollTop += ref.scrollTop;
            });
        }

        this.setState({
            scrollTop
        });
    };

    /**
     * Searching for all scrollable containers to adjust the top offset for a tooltip
     * @private
     */
    _setScrollableContainerRef = () => {
        const scrollableContainer = [];

        let scrollableContainerSearch: Node | null = this.wrapper;
        while (scrollableContainerSearch?.parentNode) {
            scrollableContainerSearch = scrollableContainerSearch.parentNode;
            if (
                (scrollableContainerSearch as HTMLElement)?.getAttribute &&
                (scrollableContainerSearch as HTMLElement).getAttribute('data-scroller')
            ) {
                scrollableContainer.push(scrollableContainerSearch as HTMLElement);
            }
        }

        this.scrollableContainerRef = scrollableContainer.length ? scrollableContainer : this.wrapper ? [this.wrapper] : [];
    };

    componentDidMount() {
        if (this.alternativesRef) {
            const availableSpace = window.innerHeight - (this.wrapper?.offsetTop || 0) - 120;

            this._setScrollableContainerRef();
            this.scrollableContainerRef.forEach((sc: HTMLElement) => sc.addEventListener('scroll', this._handleScroll));
            this.setState({
                availableSpace
            });
        }
    }

    componentWillUnmount() {
        if (this.scrollableContainerRef && Array.isArray(this.scrollableContainerRef)) {
            this.scrollableContainerRef.forEach((sc: HTMLElement) => sc.removeEventListener('scroll', this._handleScroll));
        }
    }

    _hasAlternatives() {
        const { topic, alternatives } = this.props;

        return alternatives.length !== 0 && alternatives.some((alt: KeywordItem) => alt.text !== topic);
    }

    _hasComplements() {
        const { complements } = this.props;
        const safeComplements = complements ?? [];

        return safeComplements.length > 0;
    }

    _hasSuperKeyphrase = () => {
        const { alternatives } = this.props;

        return alternatives.some((alt: KeywordItem) => (alt.points || 0) >= wordsUtil.SUPER_KEYWORD_THRESHOLD);
    };

    _wasDetected() {
        const { alternatives } = this.props;

        return alternatives.some((alt: KeywordItem) => alt.count && alt.count !== 0);
    }

    _renderTopic() {
        const { topic } = this.props;
        const isSuper = this._hasSuperKeyphrase();
        const isDetected = this._wasDetected();

        return (
            <TopicWrapper $detected={isDetected} $isSuper={isSuper}>
                {isSuper && <StarIcon />}
                <Topic>{topic}</Topic>
                {this._hasComplements() && (
                    <TopicIcon>
                        <PlusIcon />
                    </TopicIcon>
                )}
            </TopicWrapper>
        );
    }

    _renderList() {
        const { alternatives, complements, topic } = this.props;
        const alternativesWithoutTopic = alternatives.filter((alt: KeywordItem) => alt.text !== topic);
        const hasAlternatives = this._hasAlternatives();

        return (
            <React.Fragment>
                {hasAlternatives &&
                    getSortedDetection(alternativesWithoutTopic, false).map((alt: KeywordItem) => (
                        <Keyword title={alt.text} key={alt.text} $detected={alt.count !== 0}>
                            <span>{alt.text}</span>
                        </Keyword>
                    ))}
                {this._hasComplements() && (
                    <React.Fragment>
                        {hasAlternatives && <hr />}
                        {getSortedDetection(complements, false).map((alt: KeywordItem) => (
                            <Keyword title={alt.text} key={alt.text} $detected={alt.count !== 0}>
                                <TopicIcon>
                                    <PlusIcon />
                                </TopicIcon>
                                <span>{alt.text}</span>
                            </Keyword>
                        ))}
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    }

    _renderAlternativesList() {
        const { displayDoSayAlternatives } = this.props;

        if (!displayDoSayAlternatives) {
            return null;
        }

        if (!this._hasAlternatives() && !this._hasComplements()) return;

        return (
            <ListWrapper
                ref={(ref: HTMLElement | null) => {
                    this.alternativesRef = ref;
                }}>
                <List
                    style={{
                        maxHeight: this.state.availableSpace + (this.state.scrollTop - 10)
                    }}>
                    {this._renderList()}
                </List>
            </ListWrapper>
        );
    }

    render() {
        return (
            <Wrapper
                data-testid="KeyphraseStack"
                ref={(ref: HTMLElement | null) => {
                    this.wrapper = ref;
                }}
                $scrollTop={this.state.scrollTop}
                $detected={this._wasDetected()}
                $stack={this._hasAlternatives()}>
                {this._renderTopic()}
                {this._renderAlternativesList()}
            </Wrapper>
        );
    }
}

export default KeyphraseStack;
